/*
 * Loads Google Maps once for the whole app, with HQ's browser key (GET maps-key). Google calls gm_authFailure when it
 * refuses the key (wrong web address, Maps JavaScript API off), so the map can say so instead of showing a gray box.
 *
 * Visits whose address has no map spot yet are looked up with Google's geocoder here, once per address per page load.
 */
type Hooks = Window & { __stewardMapsReady?: () => void; gm_authFailure?: () => void };

let loading: Promise<void> | null = null;
let refused = false;
const refusedWatchers = new Set<() => void>();

export function loadGoogleMaps(key: string): Promise<void> {
  if (refused) return Promise.reject(new Error("refused"));
  loading ??= new Promise<void>((resolve, reject) => {
    const w = window as Hooks;
    w.__stewardMapsReady = () => resolve();
    w.gm_authFailure = () => {
      refused = true;
      for (const f of refusedWatchers) f();
    };
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&loading=async&callback=__stewardMapsReady`;
    s.async = true;
    s.onerror = () => {
      loading = null;
      s.remove();
      reject(new Error("Google Maps did not load"));
    };
    document.head.append(s);
  });
  return loading;
}

/** Calls back when Google refuses the key, now or later. Answers the stop function. */
export function onMapsRefused(f: () => void): () => void {
  if (refused) f();
  refusedWatchers.add(f);
  return () => refusedWatchers.delete(f);
}

const spots = new Map<string, Promise<google.maps.LatLngLiteral | null>>();

/** The map spot for an address, or null when Google can't find it. */
export function geocode(address: string): Promise<google.maps.LatLngLiteral | null> {
  let found = spots.get(address);
  if (!found) {
    found = (google.maps.importLibrary("geocoding") as Promise<google.maps.GeocodingLibrary>)
      .then(({ Geocoder }) => new Geocoder().geocode({ address }))
      .then((r) => r.results[0]?.geometry.location.toJSON() ?? null)
      .catch(() => null);
    spots.set(address, found);
  }
  return found;
}
